import React, { useEffect, useState } from 'react';
import { Alert, Button, Divider, Input, Select, Space, Typography } from 'antd';

import { useEditorDoc, useEditorDispatch, useEditorUi, useEditorMeta } from '../../EditorProvider';
import { listTipos, isCrosswordTipo, type TipoDePassatempo } from '../../../../../services/admin';
import type { CrosswordDoc, DeadCell, WordDraft } from './doc';
import type { CrosswordAction } from './reducer';
import { newRef, type CrosswordUi } from './ui';

// Painel de autoria guiado. A grade é o mapa (clicar coloca a dica); aqui você preenche
// o CONTEÚDO da dica selecionada: até duas respostas (uma à direita →, uma abaixo ↓),
// cada uma com a palavra e a dica em campos de texto. Metadados (título/tipo) no topo.

export const Inspector: React.FC = () => {
  const doc = useEditorDoc<CrosswordDoc>();
  const { do: dispatchAction } = useEditorDispatch();
  const { editavel } = useEditorMeta();
  const [ui, setUi] = useEditorUi<CrosswordUi>();
  const dispatch = (a: CrosswordAction) => dispatchAction(a);

  const [tipos, setTipos] = useState<TipoDePassatempo[]>([]);
  useEffect(() => {
    listTipos()
      .then((all) => setTipos(all.filter(isCrosswordTipo)))
      .catch(() => undefined);
  }, []);

  const sel = ui.selectedDead;
  const deadCell: DeadCell | undefined = sel
    ? doc.deadCells.find((c) => c.x === sel.x && c.y === sel.y)
    : undefined;

  const wordOf = (ref: string | null): WordDraft | undefined =>
    ref != null ? doc.words.find((w) => w.ref === ref) : undefined;

  const hasDirection = (direcao: boolean): boolean =>
    !!deadCell &&
    [deadCell.palavra1Ref, deadCell.palavra2Ref]
      .map(wordOf)
      .some((w) => w != null && w.direcao === direcao);

  const deadAt = (x: number, y: number) => doc.deadCells.some((c) => c.x === x && c.y === y);

  const addAnswer = (direcao: boolean) => {
    if (!deadCell) return;
    const startX = direcao ? deadCell.x : deadCell.x + 1;
    const startY = direcao ? deadCell.y + 1 : deadCell.y;
    const ref = newRef(ui);
    dispatch({
      type: 'HANG_WORD',
      deadX: deadCell.x,
      deadY: deadCell.y,
      startX,
      startY,
      direcao,
      ref,
    });
    setUi((prev) => ({ ...prev, focusRef: ref, nextRef: prev.nextRef + 1 }));
  };

  const removeDeadCell = () => {
    if (!deadCell) return;
    dispatch({ type: 'REMOVE_DEAD_CELL', x: deadCell.x, y: deadCell.y });
    setUi((prev) => ({ ...prev, selectedDead: null }));
  };

  const canRight =
    !!deadCell &&
    deadCell.x + 1 < doc.largura &&
    !deadAt(deadCell.x + 1, deadCell.y) &&
    !hasDirection(false);
  const canBelow =
    !!deadCell &&
    deadCell.y + 1 < doc.altura &&
    !deadAt(deadCell.x, deadCell.y + 1) &&
    !hasDirection(true);

  const answers = deadCell
    ? ([deadCell.palavra1Ref, deadCell.palavra2Ref]
        .map(wordOf)
        .filter((w): w is WordDraft => w != null))
    : [];

  return (
    <div style={{ minWidth: 300, maxWidth: 380 }}>
      <Typography.Title level={5} style={{ marginTop: 0 }}>
        Metadados
      </Typography.Title>
      <Space direction="vertical" style={{ width: '100%' }} size="small">
        <Input
          addonBefore="Título"
          value={doc.titulo}
          disabled={!editavel}
          onChange={(e) => dispatch({ type: 'SET_META', titulo: e.target.value })}
        />
        <Select
          style={{ width: '100%' }}
          placeholder="Tipo"
          value={doc.tiposDePassatemposId ?? undefined}
          disabled={!editavel}
          onChange={(v) => dispatch({ type: 'SET_META', tiposDePassatemposId: v })}
        >
          {tipos.map((t) => (
            <Select.Option key={t.id} value={t.id}>
              {t.nomeFantasia ?? t.nome ?? `#${t.id}`}
            </Select.Option>
          ))}
        </Select>
      </Space>

      <Divider style={{ margin: '12px 0' }} />

      {!deadCell ? (
        <Alert
          type="info"
          showIcon
          message="Como montar"
          description={
            <ol style={{ margin: 0, paddingLeft: 18 }}>
              <li>Clique numa casa vazia da grade para colocar uma dica.</li>
              <li>Escolha "resposta à direita" ou "abaixo".</li>
              <li>Digite a palavra e a dica nos campos.</li>
            </ol>
          }
        />
      ) : (
        <>
          <Space style={{ justifyContent: 'space-between', width: '100%' }}>
            <Typography.Title level={5} style={{ margin: 0 }}>
              Dica em ({deadCell.x}, {deadCell.y})
            </Typography.Title>
            <Button size="small" danger disabled={!editavel} onClick={removeDeadCell}>
              Remover dica
            </Button>
          </Space>

          {(canRight || canBelow) && (
            <Space style={{ marginTop: 8 }} wrap>
              {canRight && (
                <Button size="small" disabled={!editavel} onClick={() => addAnswer(false)}>
                  → resposta à direita
                </Button>
              )}
              {canBelow && (
                <Button size="small" disabled={!editavel} onClick={() => addAnswer(true)}>
                  ↓ resposta abaixo
                </Button>
              )}
            </Space>
          )}

          {answers.length === 0 && (
            <Typography.Paragraph type="secondary" style={{ fontSize: 12, marginTop: 8 }}>
              Adicione uma resposta à direita ou abaixo desta dica.
            </Typography.Paragraph>
          )}

          <Space direction="vertical" style={{ width: '100%', marginTop: 8 }} size="middle">
            {answers.map((w) => (
              <div
                key={w.ref}
                style={{ border: '1px solid #f0f0f0', borderRadius: 6, padding: 8 }}
              >
                <Space style={{ justifyContent: 'space-between', width: '100%' }}>
                  <Typography.Text strong>
                    {w.direcao ? '↓ Resposta abaixo' : '→ Resposta à direita'}
                  </Typography.Text>
                  <Button
                    size="small"
                    danger
                    type="text"
                    disabled={!editavel}
                    onClick={() => dispatch({ type: 'REMOVE_WORD', ref: w.ref })}
                  >
                    remover
                  </Button>
                </Space>
                <Input
                  style={{ marginTop: 6 }}
                  placeholder="Palavra (resposta)"
                  value={w.texto}
                  disabled={!editavel}
                  autoFocus={ui.focusRef === w.ref}
                  onChange={(e) => dispatch({ type: 'SET_WORD', ref: w.ref, texto: e.target.value })}
                />
                <Input
                  style={{ marginTop: 6 }}
                  placeholder="Dica (o texto que aparece na casa)"
                  value={w.chave}
                  disabled={!editavel}
                  onChange={(e) => dispatch({ type: 'SET_CLUE', ref: w.ref, chave: e.target.value })}
                />
              </div>
            ))}
          </Space>
        </>
      )}
    </div>
  );
};
